import React, { useContext } from "react";
import {
  Form,
  Input,
  Checkbox,
  Tooltip,
  Space,
  Button,
  notification,
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { MainLayout } from "../../layouts";
import { SuccessButton } from "../../components";
import { Link } from "react-router-dom";
import styled from "@xstyled/styled-components";
import { useMediaQuery } from "react-responsive";
import { Store } from "antd/lib/form/interface";
import { authContext } from "../../providers";
import { Backend } from "../../helpers";
import { AxiosError, AxiosResponse } from "axios";
import { IRegisterResponse } from "../../interfaces";

const RegisterBox = styled.div.attrs({
  className: "block",
})`
  @media (min-width: 640px) {
    width: 390px;
  }
`;

const Register: React.FC = (props) => {
  // load auth context
  const authCtxt = useContext(authContext);

  const isMd = useMediaQuery({ query: "(max-width: 768px)" });
  const isSm = useMediaQuery({ query: "(max-width: 640px)" });

  const onFinish = (values: Store) => {
    // start loading
    authCtxt.updateAuth({ loading: true });
    // submit data
    Backend.getInstance()
      .axios()
      .post("/api/createuser/", {
        email: values.email,
        username: values.username,
        password: values.password,
      })
      .then((response: AxiosResponse<IRegisterResponse>) => {
        // setting auth data
        authCtxt.updateAuth({
          data: {
            token: response.data.token,
          },
        });

        // notify the user
        notification.success({
          message: `Account created`,
          description: `Hi ${response.data.username}! Your account has been successfully created.`,
          placement: "topRight",
        });
      })
      .catch((reason: AxiosError<any>) => {
        console.log("register error:", reason.response?.data);
        // notify the user
        notification.error({
          message: `Registration failed`,
          description: "Failed to create your account. Try again later please.",
          placement: "topRight",
        });
      })
      .finally(() => {
        authCtxt.updateAuth({ loading: false });
      });
  };

  return (
    <MainLayout>
      <div className="w-full flex items-center">
        <div className="inline-block m-auto">
          <Space
            className="flex-auto"
            size="large"
            direction={isMd ? "vertical" : "horizontal"}
          >
            <div
              className="block px-8 sm:inline-block sm:p-0 w-full"
              style={{ width: isSm ? "100%" : "340px" }}
            >
              <h1 className="font-bold text-4xl pb-8 pt-16 text-gray-600">
                Create your account
              </h1>
              <RegisterBox>
                <Form
                  name="normal_login"
                  //   className="login-form ant-form-extend"
                  onFinish={onFinish}
                  scrollToFirstError
                  layout="vertical"
                >
                  <Form.Item
                    name="username"
                    label={
                      <span>
                        Username&nbsp;
                        <Tooltip title="What do you want others to call you?">
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>
                    }
                    rules={[
                      {
                        required: true,
                        message: "Please input your username!",
                        whitespace: true,
                      },
                    ]}
                  >
                    <Input size="large" />
                  </Form.Item>
                  <Form.Item
                    name="email"
                    label="E-mail"
                    rules={[
                      {
                        type: "email",
                        message: "The input is not valid E-mail!",
                      },
                      {
                        required: true,
                        message: "Please input your E-mail!",
                      },
                    ]}
                  >
                    <Input size="large" />
                  </Form.Item>

                  <Form.Item
                    name="password"
                    label="Password"
                    rules={[
                      {
                        required: true,
                        message: "Please input your password!",
                      },
                    ]}
                    hasFeedback
                  >
                    <Input.Password size="large" />
                  </Form.Item>

                  <Form.Item
                    name="confirm"
                    label="Confirm Password"
                    dependencies={["password"]}
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "Please confirm your password!",
                      },
                      ({ getFieldValue }) => ({
                        validator(rule, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            "The two passwords that you entered do not match!"
                          );
                        },
                      }),
                    ]}
                  >
                    <Input.Password size="large" />
                  </Form.Item>

                  <Form.Item name="agreement" valuePropName="checked">
                    <Checkbox>
                      I have read the <a href="/">agreement</a>
                    </Checkbox>
                  </Form.Item>
                  <Form.Item>
                    <SuccessButton
                      htmlType="submit"
                      className="w-full rounded-md uppercase font-bold"
                    >
                      Register
                    </SuccessButton>
                    <div className="py-3">
                      <Link to="/login">
                        <Button
                          type="default"
                          htmlType="button"
                          size="large"
                          className="w-full rounded-md text-xs font-bold my-3 uppercase font-bold"
                        >
                          Sign in
                        </Button>
                      </Link>
                    </div>
                  </Form.Item>
                </Form>
              </RegisterBox>
            </div>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

export default Register;
